import { useCallback } from "react"
import type {
  Workflow,
  WorkflowContent,
  WorkflowScope,
} from "@stockline/shared/types/ai/workflow"
import { WORKFLOW_TABLE } from "../../../lib/offline/resources/ai/workflow/db"
import { useAISync } from "../providers/AISyncProvider"

export interface CreateWorkflowInput {
  name: string
  description?: string
  content: WorkflowContent
  isActive?: boolean
  scope?: WorkflowScope
  userId: string
}

export interface UpdateWorkflowInput {
  name?: string
  description?: string | null
  content?: WorkflowContent
  isActive?: boolean
  scope?: WorkflowScope
}

export function useWorkflowMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createWorkflow = useCallback(
    async (input: CreateWorkflowInput): Promise<Workflow> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const workflow: Workflow = {
        id,
        companyId: "",
        userId: input.userId,
        name: input.name,
        description: input.description ?? null,
        content: input.content,
        isActive: input.isActive ?? true,
        scope: input.scope ?? "company",
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(WORKFLOW_TABLE, workflow)
      await changeQueue.enqueue("workflows", {
        op: "create",
        id,
        data: workflow,
      })
      void engine.pushChanges("workflows")

      return workflow
    },
    [storage, changeQueue, engine]
  )

  const updateWorkflow = useCallback(
    async (id: string, input: UpdateWorkflowInput): Promise<void> => {
      const existing = await storage.get<Workflow>(WORKFLOW_TABLE, id)
      if (!existing) return

      const updated: Workflow = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(WORKFLOW_TABLE, updated)
      await changeQueue.enqueue("workflows", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("workflows")
    },
    [storage, changeQueue, engine]
  )

  const deleteWorkflow = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(WORKFLOW_TABLE, id)
      await changeQueue.enqueue("workflows", {
        op: "delete",
        id,
      })
      void engine.pushChanges("workflows")
    },
    [storage, changeQueue, engine]
  )

  return { createWorkflow, updateWorkflow, deleteWorkflow }
}
